import { supabase } from '../lib/supabase';

/**
 * Cache for role permissions to avoid repeated database queries
 */
const rolePermissionsCache: Map<string, string[]> = new Map();

/**
 * Fetch permissions for a specific role from the database
 */
export async function getRolePermissions(role: string): Promise<string[]> {
  // Check cache first
  if (rolePermissionsCache.has(role)) {
    return rolePermissionsCache.get(role)!;
  }

  try {
    // Fetch permissions from the database
    const { data, error } = await supabase
      .schema('operations')
      .from('role_permissions')
      .select('permission')
      .eq('role', role);

    if (error) {
      console.error('Error fetching role permissions:', error);
      return [];
    }

    const permissions = data?.map((row) => row.permission.trim()) || [];

    // Cache the result
    rolePermissionsCache.set(role, permissions);

    return permissions;
  } catch (err) {
    console.error('Exception fetching role permissions:', err);
    return [];
  }
}

/**
 * Fetch all role permissions at once (called on app startup)
 */
export async function preloadAllRolePermissions(): Promise<void> {
  try {
    const { data, error } = await supabase
      .schema('operations')
      .from('role_permissions')
      .select('role, permission');

    if (error) {
      console.error('Error preloading role permissions:', error);
      return;
    }

    // Group permissions by role
    const permissionsByRole: Map<string, string[]> = new Map();

    data?.forEach((row) => {
      const role = row.role;
      const permission = row.permission;

      if (!permissionsByRole.has(role)) {
        permissionsByRole.set(role, []);
      }

      permissionsByRole.get(role)!.push(permission.trim());
    });

    // Update cache
    permissionsByRole.forEach((permissions, role) => {
      rolePermissionsCache.set(role, permissions);
    });

    console.log('Preloaded role permissions:', Object.fromEntries(permissionsByRole));
  } catch (err) {
    console.error('Exception preloading role permissions:', err);
  }
}

/**
 * Clear the permissions cache (useful after role permissions are modified)
 */
export function clearPermissionsCache(): void {
  rolePermissionsCache.clear();
}
